import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import AuthLayout from "./components/Layout/AuthLayout";
import MainLayout from "./components/Layout/MainLayout";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import InactiveUserRedirect from "./components/Auth/InactiveUserRedirect";
import DebugPanel from "./components/DebugPanel";

// Authentication
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import ActivationEmail from "./pages/auth/ActivationEmail";
import ResendActivation from "./pages/auth/ResendActivation";
import ConfirmEmail from "./pages/auth/ConfirmEmail";
import LockScreen from "./pages/auth/LockScreen";
import Logout from "./pages/auth/Logout";

// Dashboard
import Dashboard from "./pages/Dashboard";
import PaperFull from "./pages/paper/List";
import PaperCreate from "./pages/paper/Create";
import PaperEdit from "./pages/paper/Edit";
import UserManagement from "./pages/admin/UserManagement";

import MyProfile from "./pages/MyProfile";

// // Settings
// import AccountSettings from "./pages/settings/AccountSettings";
// import ChangePassword from "./pages/settings/ChangePassword";
// import Connections from "./pages/settings/Connections";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import DesignSystemShowcase from "./pages/DesignSystemShowcase";

// import Api.js
import * as myApi from "./components/Api";

const App = () => {
  const [pathname, setPathname] = useState(window.location.pathname);
  const [user] = useState(myApi.getUser);
  const [loginPath] = useState(myApi.loginPath);
  const [dashboardPath] = useState(myApi.dashboardPath);

  React.useEffect(() => {
    setPathname(window.location.pathname);
  }, []);

  const isAuthPage = [
    "/auth/sign-in/",
    "/auth/sign-up/",
    "/auth/forgot-password/",
    "/auth/reset-password/",
    "/auth/activation/",
    "/auth/resend-activation/",
    "/auth/confirm-email/",
    "/auth/lock-screen/",
    "/auth/logout/",
    "/not-found/",
    "/features/",
    "/team/",
    "/faq/",
    "/contact/",
    "/privacy-policy/",
    "/term-conditions/",
  ].includes(pathname);

  const isAuthenticated = myApi.checkUserActive();
  const isInactiveUser = myApi.isUserInactive();

  return (
    <Router>
      {/* Debug Panel - Only visible in non-production */}
      <DebugPanel />

      {/* Redirect inactive users to activation page */}
      <InactiveUserRedirect />
      
      <Routes>
        {/* Authentication & Public Pages - No Sidebar/Header */}
        <Route path="/auth/sign-in/" element={<AuthLayout><SignIn /></AuthLayout>} />
        <Route path="/auth/sign-up/" element={<AuthLayout><SignUp /></AuthLayout>} />
        <Route path="/auth/forgot-password/" element={<AuthLayout><ForgotPassword /></AuthLayout>} />
        <Route path="/auth/reset-password/" element={<AuthLayout><ResetPassword /></AuthLayout>} />
        <Route path="/auth/activation/" element={<AuthLayout><ActivationEmail /></AuthLayout>} />
        <Route path="/auth/resend-activation/" element={<AuthLayout><ResendActivation /></AuthLayout>} />
        <Route path="/auth/confirm-email/" element={<AuthLayout><ConfirmEmail /></AuthLayout>} />
        <Route path="/auth/lock-screen/" element={<AuthLayout><LockScreen /></AuthLayout>} />
        <Route path="/auth/sign-out/" element={<AuthLayout><Logout /></AuthLayout>} />

        <Route path="/privacy-policy/" element={<AuthLayout><PrivacyPolicy /></AuthLayout>} />
        <Route path="/term-conditions/" element={<AuthLayout><TermsConditions /></AuthLayout>} />
        <Route path="/design-system/" element={<AuthLayout><DesignSystemShowcase /></AuthLayout>} />

        {/* Protected Routes - With Sidebar/Header */}
        <Route path="/dashboard/" element={
          <MainLayout user={user}>
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          </MainLayout>
        } />
        <Route path="/admin/users/" element={
          <MainLayout user={user}>
            <ProtectedRoute requireAdmin={true}>
              <UserManagement />
            </ProtectedRoute>
          </MainLayout>
        } />
        <Route path="/paper/list/" element={
          <MainLayout user={user}>
            <ProtectedRoute>
              <PaperFull />
            </ProtectedRoute>
          </MainLayout>
        } />
        <Route path="/paper/add/" element={
          <MainLayout user={user}>
            <ProtectedRoute>
              <PaperCreate />
            </ProtectedRoute>
          </MainLayout>
        } />
        <Route path="/paper/edit/:paperCode/" element={
          <MainLayout user={user}>
            <ProtectedRoute>
              <PaperEdit />
            </ProtectedRoute>
          </MainLayout>
        } />
        <Route path="/profile/" element={
          <MainLayout user={user}>
            <ProtectedRoute>
              <MyProfile />
            </ProtectedRoute>
          </MainLayout>
        } />

        {/* Redirects */}
        <Route path="*" element={
          <Navigate to={isAuthenticated ? (user?.role === 'admin' ? dashboardPath : '/paper/list/') : loginPath} replace />
        } />
      </Routes>

      {/* Global redirect for unauthenticated users */}
      {!isAuthenticated && !isAuthPage && !isInactiveUser && (
        <Navigate to={loginPath} replace />
      )}

      {/* Redirect authenticated active users away from activation page */}
      {isAuthenticated && pathname === "/auth/activation/" && (
        <Navigate to={user?.role === 'admin' ? dashboardPath : '/paper/list/'} replace />
      )}

      {/* Redirect authenticated users away from login page */}
      {isAuthenticated && pathname === loginPath && (
        <Navigate to={user?.role === 'admin' ? dashboardPath : '/paper/list/'} replace />
      )}
    </Router>
  );
};

export default App;
